import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  createDiaryEntry,
  deleteDiaryEntry,
  listDiary,
  submitDiaryEntry,
  updateDiaryEntry,
  type CoverageStatus,
  type DiaryCoverage,
  type DiaryDay,
  type DiaryInput,
  type DiaryView,
} from '../api.js';
import { formatDate } from '../labels.js';
import { useArrivedToCreate } from './cross-links.js';
import { Attachments } from './Attachments.js';

/**
 * The daily site diary.
 *
 * `03` calls the mobile site screen the single most important in the product,
 * so this is a LIST of days rather than a table: a table of six columns has to
 * scroll sideways on a phone, and this is the screen somebody uses standing on
 * a site holding one.
 *
 * The screen's job, in order:
 *   1. say whether this project's site data can be trusted at all
 *   2. make the missing days visibly missing (`03` ST-04)
 *   3. make writing today's entry the easiest thing on it
 *
 * Every state is carried by a WORD and a SYMBOL as well as a colour. A red bar
 * that only reads as red tells a colour-blind site manager nothing.
 */

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const COVERAGE_SYMBOLS: Readonly<Record<CoverageStatus, string>> = {
  not_started: '○',
  green: '✓',
  amber: '▲',
  red: '■',
};

const COVERAGE_WORDS: Readonly<Record<CoverageStatus, string>> = {
  not_started: 'Not started',
  green: 'Up to date',
  amber: 'Falling behind',
  red: 'No site data',
};

interface Props {
  readonly projectId: string;
  /**
   * What the server says this caller may do. Not re-derived here: a second copy
   * of the permission rules in the browser is a copy that eventually disagrees
   * with the one that actually enforces them.
   */
  readonly can: { readonly write: boolean; readonly edit: boolean; readonly remove: boolean };
}

export function SiteDiary({ projectId, can }: Props): JSX.Element {
  const [view, setView] = useState<DiaryView | null>(null);
  /**
   * Distinct from "still loading". A QS or a consultant holds no `diary`
   * permission at all, and without this the section says "Loading the site
   * diary…" for the rest of the session.
   */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  /**
   * `?new=diary` — arrived from the site home to write today's entry.
   *
   * Recorded as a WANT rather than acted on immediately, because the date has
   * to come from the server (`view.today`) and the server has not answered at
   * mount. A phone on the wrong timezone would otherwise open yesterday, and a
   * diary entry filed against the wrong day is worse than no entry at all.
   */
  const [wantsToday, setWantsToday] = useState(false);
  const wantToday = useCallback(() => setWantsToday(true), []);
  useArrivedToCreate('diary', wantToday);

  const load = useCallback(async () => {
    try {
      setView(await listDiary(projectId));
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the site diary.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The want, honoured once the server has said which day today is.
  useEffect(() => {
    if (!wantsToday || !view) return;
    setWantsToday(false);
    setOpen(view.today);
  }, [wantsToday, view]);

  async function run(work: () => Promise<DiaryView>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setView(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Site diary</h3>
      {view && open === null && can.write && (
        <button type="button" className="inline" onClick={() => setOpen(view.today)}>
          Write today&rsquo;s diary
        </button>
      )}
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">Your role does not include this project&rsquo;s site diary.</p>
      </>
    );
  }

  if (!view) {
    return (
      <>
        {head}
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Loading the site diary…</p>
        )}
      </>
    );
  }

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      <Banner coverage={view.coverage} />

      <ul className="diary-days">
        {view.days.map((day) => (
          <li key={day.date} className={`diary-day diary-${day.state}`}>
            {open === day.date && !can.edit ? (
              // A QS or a Director may read the site diary and not write it
              // (`04`). They get the day, not a form full of disabled boxes.
              <ReadOnly
                day={day}
                projectId={projectId}
                canAttach={can.edit}
                onClose={() => setOpen(null)}
              />
            ) : open === day.date ? (
              <EntryForm
                day={day}
                trades={view.trades.map((t) => t.name)}
                // Defaulted rather than assumed. This is the screen used on a
                // phone on site, and a missing field — an older cached bundle,
                // a server mid-deploy — must cost the picker, not the whole
                // day's diary.
                openActivities={view.open_activities ?? []}
                canDelete={can.remove}
                busy={busy}
                onCancel={() => {
                  setOpen(null);
                  setError(null);
                }}
                onSave={async (input) => {
                  const saved = day.entry
                    ? await run(() => updateDiaryEntry(projectId, day.date, input))
                    : await run(() => createDiaryEntry(projectId, { ...input, date: day.date }));
                  if (saved) setOpen(null);
                }}
                onSubmit={async (input) => {
                  const saved = day.entry
                    ? await run(() => updateDiaryEntry(projectId, day.date, input))
                    : await run(() => createDiaryEntry(projectId, { ...input, date: day.date }));
                  if (saved && (await run(() => submitDiaryEntry(projectId, day.date)))) {
                    setOpen(null);
                  }
                }}
                onDelete={async () => {
                  if (await run(() => deleteDiaryEntry(projectId, day.date))) setOpen(null);
                }}
              />
            ) : (
              <DayRow
                day={day}
                today={view.today}
                can={can}
                busy={busy}
                onOpen={() => {
                  setError(null);
                  setOpen(day.date);
                }}
                onSubmit={() => void run(() => submitDiaryEntry(projectId, day.date))}
              />
            )}
          </li>
        ))}
      </ul>

      <p className="hint">
        A day with no entry is not a quiet day — it is a day nobody recorded. Two working days
        without one counts against this project&rsquo;s health, three counts as red.{' '}
        {view.coverage.rest_days.map((d) => WEEKDAYS[d]).join(' and ')} is treated as the rest
        day and is never counted.
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * The verdict, in words.
 *
 * `06` §"The explanation contract" requires that wherever a status appears, the
 * user can reach which rule fired, which records drive it, and when it was
 * worked out. On this screen all three are the same paragraph.
 */
/**
 * The one-line verdict.
 *
 * `06` §Signal 4 grades on the TRAILING gap — days since the last entry — so a
 * project caught up yesterday is green even with holes behind it. That is the
 * right rule and the wrong thing to say out loud: "up to date" beside six
 * blank days reads as a clean sheet. The status stays green; the sentence
 * names the holes.
 */
function verdictClause(coverage: DiaryCoverage): string {
  const days = (n: number): string => `${n} working ${n === 1 ? 'day' : 'days'}`;

  if (coverage.gap_working_days > 0) {
    return `${days(coverage.gap_working_days)} with no site diary`;
  }
  if (coverage.missing_dates.length > 0) {
    return `yesterday is written up, but ${days(
      coverage.missing_dates.length,
    )} before it never were`;
  }
  return 'every working day is written up';
}

function Banner({ coverage }: { readonly coverage: DiaryCoverage }): JSX.Element {
  const { status } = coverage;

  return (
    <div className={`diary-coverage diary-coverage-${status}`} role="status">
      <p className="diary-verdict">
        <span className="diary-symbol" aria-hidden="true">
          {COVERAGE_SYMBOLS[status]}
        </span>{' '}
        <strong>{COVERAGE_WORDS[status]}</strong>
        {status !== 'not_started' && <> — {verdictClause(coverage)}</>}
      </p>

      {status === 'not_started' ? (
        <p className="hint">
          Nothing is being counted yet. The diary starts counting from the day the Site execution
          stage begins, or from the first entry anybody writes — whichever comes first.
        </p>
      ) : (
        <p className="hint">
          {coverage.present_working_days} of {coverage.expected_working_days} working days since{' '}
          {formatDate(coverage.window_start)} are written up.{' '}
          {coverage.last_entry_date
            ? `Last written up: ${formatDate(coverage.last_entry_date)}.`
            : 'Nothing has been written up at all.'}{' '}
          {coverage.started_by === 'site_stage'
            ? `Counting from ${formatDate(coverage.started_on)}, when site work started.`
            : `Counting from ${formatDate(coverage.started_on)}, the first diary written.`}
        </p>
      )}

      {coverage.missing_dates.length > 0 && (
        <p className="hint">
          Missing:{' '}
          {coverage.missing_dates.map((d) => formatDate(d)).join(' · ')}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface RowProps {
  readonly day: DiaryDay;
  readonly today: string;
  readonly can: { readonly write: boolean; readonly edit: boolean };
  readonly busy: boolean;
  readonly onOpen: () => void;
  readonly onSubmit: () => void;
}

function DayRow({ day, today, can, busy, onOpen, onSubmit }: RowProps): JSX.Element {
  const label = `${WEEKDAYS[day.weekday]} ${formatDate(day.date)}`;
  const entry = day.entry;

  return (
    <div className="diary-row">
      <div className="diary-when">
        <span className="diary-date">{label}</span>
        {day.date === today && <span className="diary-today-tag">Today</span>}
      </div>

      <div className="diary-what">
        <span className="diary-state">
          <span className="diary-symbol" aria-hidden="true">
            {STATE_SYMBOLS[day.state]}
          </span>{' '}
          {STATE_WORDS[day.state]}
        </span>

        {entry && (
          <>
            <span className="diary-people">
              {entry.total_manpower} on site
              {entry.manpower.length > 0 && (
                <span className="tree-code">
                  {' '}
                  {entry.manpower.map((m) => `${m.trade_name} ${m.headcount}`).join(' · ')}
                </span>
              )}
            </span>
            {entry.work_summary && <span className="diary-summary">{entry.work_summary}</span>}
            {entry.delays_note && (
              <span className="diary-delay">
                <strong>Delay:</strong> {entry.delays_note}
              </span>
            )}
            {entry.recorded_days_late > 0 && (
              <span className="diary-late">
                Written {entry.recorded_days_late}{' '}
                {entry.recorded_days_late === 1 ? 'day' : 'days'} after the day it covers
              </span>
            )}
            {entry.edited_after_submission && (
              <span className="diary-late">Amended after it was submitted</span>
            )}
          </>
        )}
      </div>

      <div className="row-actions">
        {entry ? (
          <>
            <button type="button" className="link-button" disabled={busy} onClick={onOpen}>
              {can.edit ? 'Open' : 'Read'}
              <span className="visually-hidden"> the diary for {label}</span>
            </button>
            {!entry.submitted_at && can.edit && (
              <button type="button" className="link-button" disabled={busy} onClick={onSubmit}>
                Submit<span className="visually-hidden"> the diary for {label}</span>
              </button>
            )}
          </>
        ) : (
          // Offered on a rest day too. Sites do work Fridays, and the day
          // somebody worked when they were not expected to is the one most
          // worth having a record of.
          can.write && (
            <button type="button" className="link-button" disabled={busy} onClick={onOpen}>
              Write it<span className="visually-hidden"> — the diary for {label}</span>
            </button>
          )
        )}
      </div>
    </div>
  );
}

/** The day as written, for somebody who may read it but not change it. */
function ReadOnly({
  day,
  projectId,
  canAttach,
  onClose,
}: {
  readonly day: DiaryDay;
  readonly projectId: string;
  readonly canAttach: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const entry = day.entry;
  const rows: [string, string | null][] = [
    [
      'Manpower',
      entry && entry.manpower.length > 0
        ? `${entry.manpower.map((m) => `${m.trade_name} ${m.headcount}`).join(' · ')} — ${
            entry.total_manpower
          } in total`
        : null,
    ],
    [
      'Activities worked',
      entry && entry.activities.length > 0
        ? entry.activities.map((a) => a.name).join(' · ')
        : null,
    ],
    ['Work done', entry?.work_summary ?? null],
    ['Delays and problems', entry?.delays_note ?? null],
    ['Visitors', entry?.visitors_note ?? null],
  ];

  return (
    <div className="panel diary-form">
      <p className="panel-title">
        {WEEKDAYS[day.weekday]} {formatDate(day.date)}
      </p>
      <dl className="detail-grid">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value ?? '—'}</dd>
          </div>
        ))}
      </dl>

      {/*
        Site photos, INTERNAL by default — unlike a snag's. A diary is the
        contractor's own record of the day, so what the client sees of it is a
        decision made per file rather than in advance.
      */}
      {entry && (
        <Attachments
          projectId={projectId}
          entityType="diary"
          entityId={entry.id}
          can={{ write: canAttach }}
          noun="site photos"
        />
      )}

      {entry?.submitted_at ? (
        <p className="hint">
          Submitted by {entry.submitted_by_name ?? 'someone'}
          {entry.edited_after_submission && ', and amended since'}.
        </p>
      ) : (
        <p className="hint">A draft. It is not the site record until somebody submits it.</p>
      )}
      <div className="button-row">
        <button type="button" className="secondary inline" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

const STATE_SYMBOLS: Readonly<Record<DiaryDay['state'], string>> = {
  submitted: '✓',
  draft: '○',
  missing: '■',
  awaited: '·',
  rest_day: '—',
};

const STATE_WORDS: Readonly<Record<DiaryDay['state'], string>> = {
  submitted: 'Written up',
  draft: 'Draft — not submitted',
  missing: 'No entry',
  awaited: 'Not written yet',
  rest_day: 'Rest day',
};

// ---------------------------------------------------------------------------

interface ManpowerRow {
  readonly trade: string;
  readonly headcount: number;
}

interface FormProps {
  readonly day: DiaryDay;
  readonly trades: readonly string[];
  /** Everything still open on the register — what the WORK DONE picker offers. */
  readonly openActivities: readonly { readonly activity_id: string; readonly name: string }[];
  readonly canDelete: boolean;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSave: (input: DiaryInput) => void | Promise<void>;
  readonly onSubmit: (input: DiaryInput) => void | Promise<void>;
  readonly onDelete: () => void | Promise<void>;
}

function EntryForm({
  day,
  trades,
  openActivities,
  canDelete,
  busy,
  onCancel,
  onSave,
  onSubmit,
  onDelete,
}: FormProps): JSX.Element {
  const entry = day.entry;
  const submitted = Boolean(entry?.submitted_at);

  const [work, setWork] = useState(entry?.work_summary ?? '');
  const [delays, setDelays] = useState(entry?.delays_note ?? '');
  const [visitors, setVisitors] = useState(entry?.visitors_note ?? '');
  const [manpower, setManpower] = useState<ManpowerRow[]>(
    entry?.manpower.map((m) => ({ trade: m.trade_name, headcount: m.headcount })) ?? [],
  );
  const [newTrade, setNewTrade] = useState('');
  /**
   * The WORK DONE list (`12` W11). Ticks, not typing: the activity has to exist
   * on the register, because a diary that invented rows would put unplanned
   * work into the list the Site Execution gate reads.
   */
  const [worked, setWorked] = useState<readonly string[]>(
    entry?.activities.map((a) => a.activity_id) ?? [],
  );

  const label = `${WEEKDAYS[day.weekday]} ${formatDate(day.date)}`;
  const total = manpower.reduce((sum, row) => sum + row.headcount, 0);
  const id = (field: string): string => `diary-${day.date}-${field}`;

  function input(): DiaryInput {
    return {
      work_summary: work.trim() || null,
      delays_note: delays.trim() || null,
      visitors_note: visitors.trim() || null,
      manpower: manpower.map((row) => ({ trade: row.trade, headcount: row.headcount })),
      activity_ids: worked,
    };
  }

  function setCount(trade: string, headcount: number): void {
    setManpower((rows) =>
      rows.map((row) =>
        row.trade === trade ? { ...row, headcount: Math.max(0, Math.min(9999, headcount)) } : row,
      ),
    );
  }

  function addTrade(): void {
    const name = newTrade.trim();
    if (!name) return;
    if (manpower.some((row) => row.trade.toLowerCase() === name.toLowerCase())) {
      setNewTrade('');
      return;
    }
    setManpower((rows) => [...rows, { trade: name, headcount: 0 }]);
    setNewTrade('');
  }

  return (
    <div className="panel diary-form">
      <p className="panel-title">{label}</p>

      {submitted && (
        <p className="hint">
          This day has been submitted. Changes from here are recorded as an amendment, with what
          it said before and what it says now.
        </p>
      )}

      {/*
        NO WEATHER AND NO TEMPERATURE. Removed 2026-09-01 at the user's request:
        *"no need for the weather and temprutre not needed"*.

        They are standard on a site diary and they earn their place on a job with
        work outside. This is FIT-OUT — the work is indoors, and two fields that
        never change the answer are two fields somebody fills in every day for
        nothing. Whatever the weather did cause still lands in "Delays and
        problems", which is the line a delay claim is actually built from.

        The COLUMNS are deliberately still there and still nullable, so the days
        already recorded keep what they said. Dropping them is a separate and
        irreversible decision, not one to take in the same breath as tidying a
        form.
      */}

      {/* Steppers, not keyboards — `12` W4. Somebody is doing this one-handed. */}
      <fieldset className="diary-manpower">
        <legend>Manpower</legend>

        {manpower.length === 0 && (
          <p className="hint">Nobody added yet. Add the trades that were on site today.</p>
        )}

        {manpower.map((row) => (
          <div className="diary-trade" key={row.trade}>
            <span className="diary-trade-name" id={id(`t-${row.trade}`)}>
              {row.trade}
            </span>
            <div className="diary-stepper">
              <button
                type="button"
                className="diary-step"
                disabled={busy || row.headcount === 0}
                onClick={() => setCount(row.trade, row.headcount - 1)}
              >
                <span aria-hidden="true">−</span>
                <span className="visually-hidden">One fewer {row.trade}</span>
              </button>
              <label className="visually-hidden" htmlFor={id(`n-${row.trade}`)}>
                {row.trade} on site
              </label>
              <input
                id={id(`n-${row.trade}`)}
                className="diary-count"
                type="number"
                inputMode="numeric"
                min={0}
                max={9999}
                value={row.headcount}
                disabled={busy}
                onChange={(e) => setCount(row.trade, Math.trunc(Number(e.target.value) || 0))}
              />
              <button
                type="button"
                className="diary-step"
                disabled={busy}
                onClick={() => setCount(row.trade, row.headcount + 1)}
              >
                <span aria-hidden="true">+</span>
                <span className="visually-hidden">One more {row.trade}</span>
              </button>
              <button
                type="button"
                className="link-button danger"
                disabled={busy}
                onClick={() => setManpower((rows) => rows.filter((r) => r.trade !== row.trade))}
              >
                Remove<span className="visually-hidden"> {row.trade}</span>
              </button>
            </div>
          </div>
        ))}

        <p className="diary-total">
          <strong>Total on site: {total}</strong>
        </p>

        <div className="field">
          <label htmlFor={id('add-trade')}>Add a trade</label>
          <div className="diary-add-trade">
            <input
              id={id('add-trade')}
              list={id('trade-list')}
              value={newTrade}
              onChange={(e) => setNewTrade(e.target.value)}
              maxLength={80}
              disabled={busy}
              placeholder="Joinery"
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                // Inside a panel, not a form — but Enter is what a phone keyboard
                // offers, and it should do the obvious thing.
                e.preventDefault();
                addTrade();
              }}
            />
            <datalist id={id('trade-list')}>
              {trades.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <button
              type="button"
              className="secondary inline"
              disabled={busy || !newTrade.trim()}
              onClick={addTrade}
            >
              Add
            </button>
          </div>
          <p className="hint">
            A trade you have not used on this job before is created the first time you name it.
          </p>
        </div>
      </fieldset>

      {/*
        WORK DONE, in two halves (`12` W11).

        The ticks are against the activities register, so "which days was
        anybody on the ceiling grid?" becomes a query rather than a search
        through paragraphs. The box below stays for everything that is not a
        named activity — and for projects that have not written a programme at
        all, where the ticks would be the only thing on the form and it would be
        empty.
      */}
      {openActivities.length > 0 && (
        <fieldset className="diary-manpower">
          <legend>Which activities were worked?</legend>
          {openActivities.map((activity) => (
            <label className="check-row" key={activity.activity_id}>
              <input
                type="checkbox"
                checked={worked.includes(activity.activity_id)}
                disabled={busy}
                onChange={(e) =>
                  setWorked((ids) =>
                    e.target.checked
                      ? [...ids, activity.activity_id]
                      : ids.filter((existing) => existing !== activity.activity_id),
                  )
                }
              />
              <span>{activity.name}</span>
            </label>
          ))}
          <p className="hint">
            This records that work happened, not how far along it is. Put a figure on it under
            Activities, where it is kept with your name and the date.
          </p>
        </fieldset>
      )}

      <div className="field">
        <label htmlFor={id('work')}>
          {openActivities.length > 0 ? 'Anything else done' : 'Work done'}
        </label>
        <textarea
          id={id('work')}
          value={work}
          onChange={(e) => setWork(e.target.value)}
          rows={3}
          maxLength={4000}
          disabled={busy}
          placeholder="Partition framing to L3 Zone B. MEP second fix continued on L2."
        />
      </div>

      <div className="field">
        <label htmlFor={id('delays')}>Delays and problems</label>
        <textarea
          id={id('delays')}
          value={delays}
          onChange={(e) => setDelays(e.target.value)}
          rows={2}
          maxLength={4000}
          disabled={busy}
          placeholder="Ceiling crew stood down — material still not on site."
        />
        <p className="hint">
          The most useful line in a diary. It is what a delay claim is built from months later.
        </p>
      </div>

      <div className="field">
        <label htmlFor={id('visitors')}>Visitors</label>
        <input
          id={id('visitors')}
          value={visitors}
          onChange={(e) => setVisitors(e.target.value)}
          maxLength={2000}
          disabled={busy}
          placeholder="Consultant walked L3 at 11:00."
        />
      </div>

      {entry && (
        <p className="hint">
          Started by {entry.author_name ?? 'someone'}
          {entry.recorded_days_late > 0 &&
            `, ${entry.recorded_days_late} ${
              entry.recorded_days_late === 1 ? 'day' : 'days'
            } after the day it covers`}
          {entry.submitted_at && `. Submitted by ${entry.submitted_by_name ?? 'someone'}`}.
        </p>
      )}

      <div className="button-row">
        <button type="button" className="inline" disabled={busy} onClick={() => void onSave(input())}>
          {busy ? 'Saving…' : submitted ? 'Save the amendment' : 'Save as draft'}
        </button>

        {!submitted && (
          <button
            type="button"
            className="inline"
            disabled={busy}
            onClick={() => void onSubmit(input())}
          >
            Save and submit
          </button>
        )}

        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>

        {entry && canDelete && (
          <button
            type="button"
            className="link-button danger"
            disabled={busy}
            onClick={() => void onDelete()}
          >
            Delete this day
          </button>
        )}
      </div>

      {!submitted && (
        <p className="hint">
          A draft is your own notes. Only a submitted diary counts as the site record — and once
          submitted, a day cannot be taken back out of the count.
        </p>
      )}
    </div>
  );
}
